const ANALYTICS_SDK = "magnis-web";
const ANALYTICS_SDK_VERSION = "0.1.0";
export const ANALYTICS_EVENT_VERSION = 1;

export type AnalyticsMode = "off" | "internal" | "posthog";

export type AnalyticsEventName =
  | "app_opened"
  | "route_viewed"
  | "workspace_switched"
  | "onboarding_step_completed"
  | "demo_case_viewed"
  | "agent_chat_started"
  | "agent_message_sent"
  | "agent_blocked_no_invite"
  | "private_cloud_interest.submitted";

export type AnalyticsEventProperties = Record<string, unknown>;

export interface AnalyticsVersions {
  readonly app: string;
  readonly backend: string;
  readonly buildSha?: string;
}

export interface RuntimeAnalyticsConfig {
  readonly mode: AnalyticsMode;
  readonly runtimeSurface: string;
  readonly deploymentId: string;
  readonly versions: AnalyticsVersions;
  readonly internalEventSchemaVersion?: number;
  readonly posthogHost?: string;
  readonly posthogProjectApiKey?: string;
  readonly hostedDemoEntitlement?: HostedDemoEntitlement;
}

export interface HostedDemoEntitlement {
  readonly status: "credit_zero_no_invite" | "invite_credits_available";
  readonly campaignId?: string;
  readonly inviteId?: string;
  readonly creditLimitMicros?: number;
}

export interface AnalyticsFlowContext {
  readonly analytics_flow_id: string;
  readonly client_instance_id: string;
  readonly frontend_origin: string;
  readonly landing_referrer?: string;
  readonly utm_source?: string;
  readonly utm_medium?: string;
  readonly utm_campaign?: string;
  readonly utm_content?: string;
  readonly utm_term?: string;
  readonly utm_id?: string;
}

export interface AnalyticsClient {
  readonly mode: AnalyticsMode;
  readonly runtimeSurface: string;
  readonly deploymentId: string;
  readonly analyticsFlowId: string;
  readonly clientInstanceId: string;
  readonly hostedDemoEntitlement?: HostedDemoEntitlement;
  readonly capture: (
    eventName: AnalyticsEventName,
    properties: AnalyticsEventProperties,
    options: {
      readonly source: string;
      readonly internalProperties?: AnalyticsEventProperties;
    },
  ) => Promise<void>;
}

export interface InternalAnalyticsTransport {
  readonly rpc: <T>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
}

export interface PostHogLike {
  readonly register: (properties: Record<string, unknown>) => void;
  readonly capture: (eventName: string, properties?: Record<string, unknown>) => void;
}

const FLOW_KEY = "magnis.analytics.flow_id";
const CLIENT_KEY = "magnis.analytics.client_instance_id";
const REFERRER_KEY = "magnis.analytics.landing_referrer";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
] as const;

type UTMKey = (typeof UTM_KEYS)[number];

function newId(prefix: string): string {
  if (typeof globalThis.crypto.randomUUID !== "function") {
    throw new Error("crypto.randomUUID is required for analytics IDs");
  }
  return `${prefix}_${globalThis.crypto.randomUUID()}`;
}

function readParam(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim();
  return value && value.length > 0 ? value : null;
}

function getStored(storage: Storage, key: string): string | null {
  const value = storage.getItem(key)?.trim();
  return value && value.length > 0 ? value : null;
}

export function captureAnalyticsFlowContext(
  location: Location = window.location,
  storage: Storage = window.localStorage,
  referrer: string = document.referrer,
): AnalyticsFlowContext {
  const params = new URL(location.href).searchParams;
  // @tested-by: tst_fe_analytics_009_flow_id_alias_imports_but_does_not_generate_alias
  const flowFromUrl =
    readParam(params, "mgn_flow") ?? readParam(params, "analytics_flow_id");
  const analyticsFlowId = flowFromUrl ?? getStored(storage, FLOW_KEY) ?? newId("flow");
  storage.setItem(FLOW_KEY, analyticsFlowId);

  const clientInstanceId = getStored(storage, CLIENT_KEY) ?? newId("client");
  storage.setItem(CLIENT_KEY, clientInstanceId);

  const landingReferrer = getStored(storage, REFERRER_KEY) ?? referrer.trim();
  if (landingReferrer.length > 0) {
    storage.setItem(REFERRER_KEY, landingReferrer);
  }

  const utm: Partial<Record<UTMKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = readParam(params, key) ?? getStored(storage, `magnis.analytics.${key}`);
    if (value) {
      storage.setItem(`magnis.analytics.${key}`, value);
      utm[key] = value;
    }
  }

  return {
    analytics_flow_id: analyticsFlowId,
    client_instance_id: clientInstanceId,
    frontend_origin: location.origin,
    ...(landingReferrer.length > 0 ? { landing_referrer: landingReferrer } : {}),
    ...utm,
  };
}

export function createDisabledAnalyticsFlowContext(
  location: Location = window.location,
): AnalyticsFlowContext {
  return {
    analytics_flow_id: "analytics_off",
    client_instance_id: "analytics_off",
    frontend_origin: location.origin,
  };
}

function baseProperties(
  config: RuntimeAnalyticsConfig,
  flow: AnalyticsFlowContext,
): Record<string, unknown> {
  return {
    event_version: ANALYTICS_EVENT_VERSION,
    analytics_sdk: ANALYTICS_SDK,
    analytics_sdk_version: ANALYTICS_SDK_VERSION,
    app_version: config.versions.app,
    backend_version: config.versions.backend,
    frontend_origin: flow.frontend_origin,
    runtime_surface: config.runtimeSurface,
    deployment_id: config.deploymentId,
    client_instance_id: flow.client_instance_id,
    analytics_flow_id: flow.analytics_flow_id,
    ...(config.versions.buildSha ? { build_sha: config.versions.buildSha } : {}),
    ...(flow.landing_referrer ? { landing_referrer: flow.landing_referrer } : {}),
    ...(flow.utm_source ? { utm_source: flow.utm_source } : {}),
    ...(flow.utm_medium ? { utm_medium: flow.utm_medium } : {}),
    ...(flow.utm_campaign ? { utm_campaign: flow.utm_campaign } : {}),
    ...(flow.utm_content ? { utm_content: flow.utm_content } : {}),
    ...(flow.utm_term ? { utm_term: flow.utm_term } : {}),
    ...(flow.utm_id ? { utm_id: flow.utm_id } : {}),
  };
}

function assertNoExternalPrivateContent(properties: Record<string, unknown>): void {
  // @tested-by: tst_fe_analytics_008_posthog_mode_rejects_raw_content_fields
  const blocked = new Set([
    "email",
    "email_address",
    "name",
    "telegram_handle",
    "message",
    "message_text",
    "prompt",
    "prompt_text",
    "document_text",
    "note_text",
  ]);
  for (const key of Object.keys(properties)) {
    if (blocked.has(key.toLowerCase())) {
      throw new Error(`analytics property ${key} is not allowed on external events`);
    }
  }
}

export function createAnalyticsClient({
  config,
  flow,
  internalTransport,
  posthog,
}: {
  readonly config: RuntimeAnalyticsConfig;
  readonly flow: AnalyticsFlowContext;
  readonly internalTransport: InternalAnalyticsTransport;
  readonly posthog?: PostHogLike;
}): AnalyticsClient {
  const base = baseProperties(config, flow);
  const posthogSink = config.mode === "posthog" ? posthog : undefined;
  if (config.mode === "posthog") {
    if (!posthogSink) {
      throw new Error("posthog analytics mode requires a PostHog sink");
    }
    posthogSink.register(base);
  }

  return {
    mode: config.mode,
    runtimeSurface: config.runtimeSurface,
    deploymentId: config.deploymentId,
    analyticsFlowId: flow.analytics_flow_id,
    clientInstanceId: flow.client_instance_id,
    hostedDemoEntitlement: config.hostedDemoEntitlement,
    async capture(
      eventName: AnalyticsEventName,
      properties: AnalyticsEventProperties,
      options: {
        readonly source: string;
        readonly internalProperties?: AnalyticsEventProperties;
      },
    ): Promise<void> {
      if (config.mode === "off") {
        // @tested-by: tst_fe_analytics_001_client_noop_mode_skips_sinks
        return;
      }

      const merged = { ...base, ...properties };
      const internalProperties = {
        ...merged,
        ...(options.internalProperties ?? {}),
      };

      if (posthogSink) {
        assertNoExternalPrivateContent(merged);
        posthogSink.capture(eventName, merged);
      }

      await internalTransport.rpc("user_events.track", {
        event_name: eventName,
        source: options.source,
        properties: internalProperties,
      });
    },
  };
}
